import { Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, skipWhile, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EFeatureToggles, ESparePartsOrderHistoryTabs } from '../../../configurations/common';
import { OrdersFacade } from '../../../facades/orders.facade';
import { IArchiveOrder, IOrder, IOrderData } from '../../../models/order.models';
import { TranslateService } from '@ngx-translate/core';
import { IPaginationCurrent } from '../../../models/settings.model';
import { IOrdersWithSapDetails, OrderDataType } from '../../../reducers/orders.reducer';
import { ConfigurationFacade } from '../../../facades/configuration.facade';
import { StringUtils } from '../../../utils/string.utils';

@Component({
  selector: 'app-spare-part-purchase-activity',
  templateUrl: './spare-part-purchase-activity.component.html',
  styleUrls: ['./spare-part-purchase-activity.component.scss'],
  standalone: false,
})
export class SparePartPurchaseActivityComponent implements OnInit {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private ordersFacade: OrdersFacade = inject(OrdersFacade);
  private translate: TranslateService = inject(TranslateService);
  private configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);

  ESparePartsOrderHistoryTabs: typeof ESparePartsOrderHistoryTabs = ESparePartsOrderHistoryTabs;
  currentTab: string = ESparePartsOrderHistoryTabs.ORDERS;
  tabs: string[] = [
    ESparePartsOrderHistoryTabs.ORDERS,
  ];

  allLoadedOrders: any[] = [];
  orders: any[] = [];
  ordersLoading: boolean = true;
  ordersCurrentPage: number = 1;
  ordersNumPages: number = 1;
  ordersLimitPerPage: number = 1;

  hybrisOrders: IArchiveOrder[] = [];
  hybrisOrdersLoading: boolean = true;

  private unsubscribe$: Subject<void> = new Subject<void>();

  @Output() sparePartOrdersLoadedEmit: EventEmitter<boolean> = new EventEmitter<boolean>();

  ngOnInit(): void {
    this.selectCurrentTab();
    this.goToOrdersPage(1);
    this.retrieveHybrisOrders();
    this.getArchivedFeature();
  }

  goToOrdersPage(pageNum: number): void {
    this.ordersLoading = true;
    this.ordersCurrentPage = pageNum;
    combineLatest([
      this.ordersFacade.selectOrdersWithSapDetailsAtPage(pageNum),
      this.ordersFacade.selectOrdersWithSapDetailsPagination(),
      this.ordersFacade.selectLoadedOrdersWithSapDetails(),
    ]).pipe(
      skipWhile(([, pagination]) => !pagination.currentPage), // first page still loading in the background
      takeUntil(this.unsubscribe$),
    ).subscribe(([ordersWithSapDetails, pagination, allOrders]) => {
      if (ordersWithSapDetails.orders?.length) {
        this.updateOrders(ordersWithSapDetails, pagination, allOrders);
      } else if (this.ordersCurrentPage === 1) {
        // the user does not have access to any orders
        this.updateOrders(ordersWithSapDetails, pagination, allOrders);
      } else {
        const limit: number = pagination.limitPerPage;
        const offset: number | undefined = limit ? (pageNum - 1) * limit : undefined;
        this.ordersFacade.loadOrdersWithSapDetails(offset, limit);
      }
    });
  }

  retrieveHybrisOrders(): void {
    this.hybrisOrdersLoading = true;
    this.ordersFacade.selectHybrisOrders().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe((orders: IArchiveOrder[] | null) => {
      if (orders) {
        this.hybrisOrders = orders;
        this.hybrisOrdersLoading = false;
        this.checkIfAllOrdersLoaded();
      }
    });
  }

  private updateOrders(
    ordersWithSapDetails: IOrdersWithSapDetails,
    pagination: IPaginationCurrent,
    allOrders: IOrdersWithSapDetails,
  ): void {
    this.orders = this.filterByDate(
      this.mapOrders(ordersWithSapDetails.orders, ordersWithSapDetails.sapOrdersDetails),
    );
    this.allLoadedOrders = this.filterByDate(
      this.mapOrders(allOrders.orders, allOrders.sapOrdersDetails),
    );
    this.ordersNumPages = pagination.lastPage;
    this.ordersLimitPerPage = pagination.limitPerPage;

    this.ordersLoading = false;
    this.checkIfAllOrdersLoaded();
  }

  private mapOrders(orders: OrderDataType, sapOrderDetailsList: OrderDataType): any[] {
    return (orders ?? []).map((order: IOrder | IOrderData) => {
      const sapOrderDetails: IOrder | IOrderData | undefined = (sapOrderDetailsList ?? []).find(orderDetails => orderDetails.id === order.id);

      return {
        ...order,
        attributes: {
          ...order?.attributes,
          ...sapOrderDetails?.attributes,
        },
        mappedStateDisplayName: this.translate.instant(
          `spare-part-list.order-status-${StringUtils.replaceSpaces(order.attributes?.stateDisplayName, '-')}`,
        ),
      };
    });
  }

  private filterByDate(orderArray: any[]): any[] {
    const yearAgo: Date = new Date();
    yearAgo.setFullYear(yearAgo.getFullYear() - 1);
    yearAgo.setUTCHours(0, 0, 0, 0);

    const filteredOrders: any[] = orderArray.filter(
      order =>
        new Date(order.attributes.createdAt).getTime() >= yearAgo.getTime(),
    );
    return filteredOrders ?? [];
  }

  selectCurrentTab(): void {
    this.route.queryParams
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(params => {
        this.currentTab = params.subtab
          ? params.subtab
          : ESparePartsOrderHistoryTabs.ORDERS;
      });
  }

  changeTab(tab: string): void {
    this.currentTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {subtab: tab},
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  checkIfAllOrdersLoaded(): void {
    if (!this.ordersLoading && !this.hybrisOrdersLoading) {
      this.sparePartOrdersLoadedEmit.emit(true);
    }
  }

  getArchivedFeature(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.PURCHASE_ACTIVITY_ARCHIVE).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(archiveEnabled => {
      if (archiveEnabled) {
        if (!this.tabs.includes(ESparePartsOrderHistoryTabs.ARCHIVE)) {
          this.tabs.push(ESparePartsOrderHistoryTabs.ARCHIVE);
        }
      }
    });
  }
}
